import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { locale, t, tr } from '../i18n';
import type { CarBrief, CarStatus, DocType, DriverBrief, EmploymentType, FuelCategory, FuelPaymentMethod, FuelType, ServiceType, TireSeason, VignetteCountry, VignettePeriod } from '../types';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// ---------------------------------------------------------------- dates
/** API datetimes are UTC with a Z suffix; plain dates (YYYY-MM-DD) are calendar dates. */
function toDate(value: string | Date): Date {
  if (value instanceof Date) return value;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  return new Date(value);
}

export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return '–';
  const date = toDate(value);
  return Number.isNaN(date.getTime()) ? '–' : date.toLocaleDateString(locale());
}

export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return '–';
  const date = toDate(value);
  if (Number.isNaN(date.getTime())) return '–';
  return `${date.toLocaleDateString(locale())} ${date.toLocaleTimeString(locale(), { hour: '2-digit', minute: '2-digit' })}`;
}

export function formatTime(value: string | null | undefined): string {
  return value ? value.slice(0, 5) : '–';
}

/** YYYY-MM-DD for <input type="date"> in local time. */
export function toDateInput(date: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** YYYY-MM-DDTHH:mm for <input type="datetime-local"> in local time. */
export function toDateTimeInput(value: string | Date = new Date()): string {
  const date = toDate(value);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${toDateInput(date)}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Local datetime-local value -> ISO string with timezone for the API. */
export function localInputToIso(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

export function daysUntil(value: string | null | undefined): number | null {
  if (!value) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((toDate(value).getTime() - today.getTime()) / 86_400_000);
}

export function formatDuration(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined) return '–';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h ? `${h} h ${m} min` : `${m} min`;
}

// ---------------------------------------------------------------- numbers
export function formatNumber(value: number | null | undefined, digits = 0): string {
  if (value === null || value === undefined) return '–';
  return value.toLocaleString(locale(), { maximumFractionDigits: digits, minimumFractionDigits: 0 });
}

export function formatKm(value: number | null | undefined): string {
  return value === null || value === undefined ? '–' : `${formatNumber(value, 1)} km`;
}

export function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) return '–';
  return value.toLocaleString(locale(), { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 });
}

/** "September 2026" in the current language */
export function formatMonth(year: number, month: number): string {
  const name = new Date(year, month - 1, 1).toLocaleDateString(locale(), { month: 'long' });
  return `${name.charAt(0).toUpperCase()}${name.slice(1)} ${year}`;
}

export function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes) return '–';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ---------------------------------------------------------------- labels
export const CAR_STATUS_LABELS: Record<CarStatus, string> = tr({
  active: 'Aktívne',
  service: 'V servise',
  sold: 'Predané',
  retired: 'Vyradené',
});

export const FUEL_LABELS: Record<FuelType, string> = tr({
  petrol: 'Benzín',
  diesel: 'Nafta',
  electric: 'Elektro',
  hybrid: 'Hybrid',
});

export const FUEL_CATEGORY_LABELS: Record<FuelCategory, string> = tr({
  fuel: 'Palivo',
  adblue: 'AdBlue',
  other: 'Ostatné (oleje, príslušenstvo, umývanie…)',
});

export const FUEL_PAYMENT_LABELS: Record<FuelPaymentMethod, string> = tr({
  fuel_card: 'Palivová karta (Shell)',
  cash: 'Hotovosť',
  private_card: 'Súkromná karta',
  other: 'Iná firemná karta / iné',
});

export const SERVICE_TYPE_LABELS: Record<ServiceType, string> = tr({
  maintenance: 'Údržba',
  repair: 'Oprava',
  inspection: 'Kontrola / STK / EK',
  tires: 'Pneumatiky',
  other: 'Ostatné',
});

export const DOC_TYPE_LABELS: Record<DocType, string> = tr({
  protocol: 'Odovzdávací protokol',
  insurance: 'Poistenie',
  registration: 'Technický preukaz',
  inspection: 'STK / EK',
  photo: 'Fotografie',
  other: 'Ostatné',
});

export const EMPLOYMENT_LABELS: Record<EmploymentType, string> = tr({
  employee: 'Zamestnanec',
  self_employed: 'Živnostník',
});

/** Tire seasons: label, short mark drawn on the wheel, and colour (red / blue / purple, checked for colour-blind readers). */
export const TIRE_SEASONS: Record<TireSeason, { label: string; mark: string; color: string }> = {
  summer: {
    get label() { return t('Letné'); },
    get mark() { return t('L'); },
    color: '#d93f2e',
  },
  winter: {
    get label() { return t('Zimné'); },
    get mark() { return t('Z'); },
    color: '#3a86e0',
  },
  all_season: {
    get label() { return t('Celoročné'); },
    get mark() { return t('C'); },
    color: '#7e2aa8',
  },
};

/** Winter season for the summer-tire warning (15 Nov – 31 Mar, when winter tires are recommended/required on snow in SK). */
export function isWinterSeason(day: Date = new Date()): boolean {
  const m = day.getMonth() + 1;
  return m === 12 || m <= 3 || (m === 11 && day.getDate() >= 15);
}

/** European countries for fines (codes as on number plates / ISO). */
export const EUROPE_COUNTRIES: Record<string, string> = tr({
  SK: 'Slovensko', CZ: 'Česko', AT: 'Rakúsko', HU: 'Maďarsko', PL: 'Poľsko', DE: 'Nemecko', SI: 'Slovinsko', HR: 'Chorvátsko',
  IT: 'Taliansko', FR: 'Francúzsko', CH: 'Švajčiarsko', BE: 'Belgicko', NL: 'Holandsko', LU: 'Luxembursko', DK: 'Dánsko',
  SE: 'Švédsko', NO: 'Nórsko', FI: 'Fínsko', ES: 'Španielsko', PT: 'Portugalsko', RO: 'Rumunsko', BG: 'Bulharsko',
  RS: 'Srbsko', GR: 'Grécko', LT: 'Litva', LV: 'Lotyšsko', EE: 'Estónsko', IE: 'Írsko', GB: 'Spojené kráľovstvo', UA: 'Ukrajina',
});

export const countryName = (code: string) => EUROPE_COUNTRIES[code] ?? code;

/** EU countries where passenger cars need a vignette, with the official e-shop of each. */
const vignetteCountry = (code: VignetteCountry, shop: string, shopName: string) => ({
  get name() {
    return countryName(code);
  },
  shop,
  shopName,
});

export const VIGNETTE_COUNTRIES: Record<VignetteCountry, { name: string; shop: string; shopName: string }> = {
  SK: vignetteCountry('SK', 'https://eznamka.sk/sk/', 'eznamka.sk'),
  CZ: vignetteCountry('CZ', 'https://edalnice.gov.cz/', 'edalnice.gov.cz'),
  AT: vignetteCountry('AT', 'https://shop.asfinag.at/', 'shop.asfinag.at'),
  HU: vignetteCountry('HU', 'https://nemzetimobilfizetes.hu/', 'nemzetimobilfizetes.hu'),
  SI: vignetteCountry('SI', 'https://evinjeta.dars.si/', 'evinjeta.dars.si'),
  BG: vignetteCountry('BG', 'https://web.bgtoll.bg/', 'web.bgtoll.bg'),
  RO: vignetteCountry('RO', 'https://www.erovinieta.ro/', 'erovinieta.ro'),
};

export const VIGNETTE_PERIOD_LABELS: Record<VignettePeriod, string> = tr({
  day: '1-dňová',
  weekend: 'víkendová',
  week: '7-dňová / týždenná',
  '10days': '10-dňová',
  month: 'mesačná / 30-dňová',
  '2months': '2-mesačná',
  quarter: 'štvrťročná / 90-dňová',
  year: 'ročná',
  regional: 'regionálna (župná) ročná',
});

/** Typical length in days, used only to pre-fill "valid to" (the user can change it). */
export const VIGNETTE_PERIOD_DAYS: Record<VignettePeriod, number> = {
  day: 1,
  weekend: 3,
  week: 7,
  '10days': 10,
  month: 30,
  '2months': 60,
  quarter: 90,
  year: 365,
  regional: 365,
};

export const TABLE_LABELS: Record<string, string> = tr({
  cars: 'Vozidlá',
  drivers: 'Vodiči',
  assignments: 'Pridelenia',
  contracts: 'Zmluvy',
  reservations: 'Rezervácie',
  fines: 'Pokuty',
  vignettes: 'Diaľničné známky',
  services: 'Servisy',
  documents: 'Dokumenty',
  gps_rides: 'GPS jazdy',
  gps_months: 'GPS – mesačné záznamy',
  users: 'Používatelia',
  settings: 'Nastavenia',
});

export const ACTION_LABELS: Record<string, string> = tr({
  CREATE: 'Vytvorenie',
  UPDATE: 'Zmena',
  DELETE: 'Zmazanie',
  LOGIN: 'Prihlásenie',
  LOGIN_FAILED: 'Neúspešné prihlásenie',
  IMPORT: 'Import',
});

export const toOptions = <T extends string>(labels: Record<T, string>) =>
  (Object.entries(labels) as [T, string][]).map(([value, label]) => ({ value, label }));

export function carLabel(car: Pick<CarBrief, 'license_plate' | 'brand' | 'model'> | null | undefined): string {
  return car ? `${car.license_plate} – ${car.brand} ${car.model}` : '–';
}

export function driverName(driver: Pick<DriverBrief, 'first_name' | 'last_name'> | null | undefined): string {
  return driver ? `${driver.first_name} ${driver.last_name}`.trim() : '–';
}

// ---------------------------------------------------------------- forms
/** Turn form strings into API values: '' -> null, numeric fields -> numbers. */
export function normalizeForm<T extends Record<string, unknown>>(values: T, numberFields: (keyof T)[] = []): T {
  const result: Record<string, unknown> = {};
  for (const [key, raw] of Object.entries(values)) {
    let value = typeof raw === 'string' ? raw.trim() : raw;
    if (value === '' || value === undefined) value = null;
    if (value !== null && numberFields.includes(key as keyof T)) {
      const parsed = Number(String(value).replace(',', '.').replace(/\s/g, ''));
      value = Number.isFinite(parsed) ? parsed : null;
    }
    result[key] = value;
  }
  return result as T;
}

/** Hex colour -> "r g b" channels for CSS variables. */
function hexToRgb(hex: string): [number, number, number] | null {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!match) return null;
  const n = parseInt(match[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

const SHADE_MIX: Record<number, number> = {
  50: 0.92,
  100: 0.84,
  200: 0.68,
  300: 0.5,
  400: 0.26,
  500: 0.1,
  600: 0,
  700: -0.15,
  800: -0.3,
  900: -0.45,
};

/** Generate a 50–900 palette from one brand colour (used as the 600 shade). */
export function applyPrimaryColor(hex: string | null | undefined) {
  const rgb = hex ? hexToRgb(hex) : null;
  const root = document.documentElement;
  if (!rgb) return;
  for (const [shade, mix] of Object.entries(SHADE_MIX)) {
    const channel = (c: number) => Math.round(mix >= 0 ? c + (255 - c) * mix : c * (1 + mix));
    root.style.setProperty(`--color-primary-${shade}`, rgb.map(channel).join(' '));
  }
}
